/**
 * useNativePush — Capacitor (iOS APNs / Android FCM) push registration.
 *
 * NO-OP in the regular PWA / browser context — `Capacitor.isNativePlatform()`
 * returns false there, so this hook simply returns early without touching
 * the plugin. Only when the app is launched from the iOS / Android shell
 * does it:
 *
 *   1. attach the four push listeners (registration / error / received /
 *      action-performed),
 *   2. request notification permission (skipped if already granted),
 *   3. call PushNotifications.register() to fetch the device token,
 *   4. POST it to `/api/push/native/register` so the backend can target
 *      the device via APNs / FCM.
 *
 * Plugin docs: https://capacitorjs.com/docs/apis/push-notifications
 * Backend endpoint: POST /api/push/native/register (see routes/push.py).
 */
import { useEffect } from 'react';

import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const isNativeRuntime = () => {
  try {
    // Dynamic require so the regular web build doesn't fail when
    // `@capacitor/core` only ships its native bindings.

    const { Capacitor } = require('@capacitor/core');
    return typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();
  } catch (_) {
    return false;
  }
};

export const useNativePush = () => {
  const { isAuthenticated, user } = useAuth();
  const userId = isAuthenticated ? user?.user_id : null;

  useEffect(() => {
    if (!userId) return undefined;
    if (!isNativeRuntime()) return undefined;

    let cleanupFns = [];
    let cancelled = false;

    (async () => {
      try {
        const { PushNotifications } = require('@capacitor/push-notifications');

        const { Capacitor } = require('@capacitor/core');
        const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

        // 1. Attach listeners FIRST so we don't miss the registration event.
        const regHandle = await PushNotifications.addListener('registration', async (token) => {
          if (cancelled) return;
          try {
            await api.post('/push/native/register', {
              token: token.value,
              platform,
            });
          } catch (e) {
            console.warn('[useNativePush] backend register failed:', e?.message);
          }
        });
        const errHandle = await PushNotifications.addListener('registrationError', (err) => {
          console.warn('[useNativePush] registrationError:', err?.error);
        });
        const recvHandle = await PushNotifications.addListener(
          'pushNotificationReceived',
          (notif) => {
            // App is foregrounded — the OS doesn't show a banner by default.
            // We rely on the existing in-app toast system (sonner) which
            // useNotificationCenter already fires from WS events.

            console.log('[useNativePush] received:', notif?.title);
          },
        );
        const actHandle = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            // Deep-link routing hook (e.g. /messages/<thread>) lives here.
            const path = action?.notification?.data?.path;
            if (path) window.location.assign(path);
          },
        );
        cleanupFns = [regHandle, errHandle, recvHandle, actHandle];

        // 2. Permission gate.
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== 'granted') return;

        // 3. Hand off to OS — token arrives via the 'registration' listener.
        await PushNotifications.register();
      } catch (e) {
        console.warn('[useNativePush] init failed:', e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
      // PushNotifications listeners return PluginListenerHandle; .remove() detaches.
      cleanupFns.forEach((h) => {
        try {
          h?.remove?.();
        } catch (err) {
          // Stale plugin listener handle (Capacitor reload edge case) —
          // benign, but log for visibility per iter-53 review.
          console.debug('[useNativePush] listener remove failed:', err?.message);
        }
      });
    };
  }, [userId]);
};

export default useNativePush;
